import { useState } from "react";
import { Link } from "react-router-dom";
import { SITE } from "@/config/site";
import { hotFeed, stats } from "@/mocks/content";
import { liveSessions } from "@/mocks/lives";
import { SignalMeter } from "@/components/base/LiveIndicators";
import { cn, formatViewers } from "@/utils/format";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Cinematic%20dark%20broadcast%20control%20room%20wall%20of%20glowing%20monitors%20showing%20abstract%20live%20video%20feeds%20of%20city%20streets%20and%20stormy%20weather%2C%20moody%20low-key%20lighting%2C%20deep%20warm%20charcoal%20tones%20with%20amber%20glow%2C%20blurred%20operator%20silhouettes%2C%20high%20detail%2C%20editorial%20photojournalism%20atmosphere%2C%20wide%20dramatic%20composition&width=1600&height=900&seq=blzb-hero-01&orientation=landscape";

export default function HeroSection() {
  const [soundOn, setSoundOn] = useState(true);
  const [activeSpot, setActiveSpot] = useState(0);

  const featured = liveSessions.filter((item) => item.status === "live").slice(0, 3);
  const current = featured[activeSpot] ?? liveSessions[0];
  const ticker = [...hotFeed, ...hotFeed];

  return (
    <section className="relative overflow-hidden pt-16 md:pt-20">
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="爆料直播现场信号墙，多路实时画面正在回传"
          title="爆料直播 - 现场实时画面信号墙"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/88 via-background-950/78 to-background-50" />
        <div className="stage-grid absolute inset-0 opacity-70" aria-hidden="true" />
      </div>

      <div className="relative w-full px-4 pb-0 pt-12 md:px-6 md:pt-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full border border-accent-400/40 bg-accent-500/10 px-3 py-1.5 font-label text-[11px] tracking-wider text-accent-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500" aria-hidden="true" />
              {SITE.onlineSignals} · 现场信号持续回传
            </p>

            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl md:leading-[1.15]">
              爆料直播
              <span className="mx-2 text-primary-500">·</span>
              把现场实时画面
              <br className="hidden md:block" />
              直接送到你眼前
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
              进入爆料直播，获取现场实时画面与突发事态直击。直播镜头追踪不停歇，实时画面信号稳定，
              还能听到现场连麦声音。吃瓜网友关心的第一现场，这里直接看。
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/live"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-live-line text-base" aria-hidden="true" />
                进入直播大厅
              </Link>
              <Link
                to="/about"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500/60 hover:text-primary-500"
              >
                <i className="ri-shield-check-line text-base" aria-hidden="true" />
                了解内容原则
              </Link>
            </div>

            <dl className="mt-10 grid max-w-xl grid-cols-2 gap-x-6 gap-y-5 border-t border-background-300/50 pt-6 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="font-label text-xl font-bold text-foreground-950 md:text-2xl">
                    {item.value}
                  </dt>
                  <dd className="mt-1 text-[11px] leading-relaxed text-foreground-500">
                    {item.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200/70 bg-background-100/90 p-3 backdrop-blur md:p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 font-label text-[11px] tracking-wider text-accent-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500" aria-hidden="true" />
                  正在直播
                </span>
                <button
                  type="button"
                  onClick={() => setSoundOn((value) => !value)}
                  aria-pressed={soundOn}
                  aria-label={soundOn ? "关闭现场连麦声音" : "打开现场连麦声音"}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-500/60 hover:text-primary-500"
                >
                  <i className={soundOn ? "ri-volume-up-line" : "ri-volume-mute-line"} aria-hidden="true" />
                  {soundOn ? "连麦声音开" : "连麦声音关"}
                </button>
              </div>

              <div className="relative mt-3 aspect-video w-full overflow-hidden rounded-md bg-background-200">
                <img
                  src={current.cover}
                  alt={`${current.title} 的现场实时画面`}
                  title={`${current.title} - 现场实时画面`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-background-950/30" />
                <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-accent-500 px-2.5 py-1 font-label text-[10px] text-background-50">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-background-50" aria-hidden="true" />
                  LIVE
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                  <SignalMeter
                    value={current.signal}
                    sound={soundOn}
                    className="rounded-full bg-background-950/60 px-2 py-1 backdrop-blur"
                  />
                  <span className="inline-flex items-center gap-1 whitespace-nowrap font-label text-[11px] text-foreground-800">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatViewers(current.viewers)}
                  </span>
                </div>
              </div>

              <h2 className="mt-3 font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
                {current.title}
              </h2>
              <p className="mt-1 text-xs text-foreground-500">
                {current.location} · {current.duration}
              </p>

              <ul className="mt-3 flex flex-col divide-y divide-background-200/70 border-t border-background-200/70">
                {featured.map((item, index) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setActiveSpot(index)}
                      aria-pressed={index === activeSpot}
                      className={cn(
                        "flex w-full cursor-pointer items-center justify-between gap-3 py-2.5 text-left transition-colors",
                        index === activeSpot ? "text-primary-500" : "text-foreground-600 hover:text-foreground-900"
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <i
                          className={index === activeSpot ? "ri-radio-button-line" : "ri-checkbox-blank-circle-line"}
                          aria-hidden="true"
                        />
                        <span className="truncate text-xs">{item.title}</span>
                      </span>
                      <span className="shrink-0 font-label text-[11px]">{item.signal}%</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-12 border-y border-background-200/70 bg-background-100/80 py-3 backdrop-blur">
        <div className="flex w-full items-center gap-3 overflow-hidden px-4 md:px-6">
          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-2.5 py-1 font-label text-[11px] text-background-50">
            <i className="ri-flashlight-line" aria-hidden="true" />
            现场快讯
          </span>
          <div className="flex w-max animate-marquee items-center gap-8">
            {ticker.map((item, index) => (
              <Link
                key={`${item.id}-${index}`}
                to={`/live/${item.liveId}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-primary-500"
              >
                <span className="text-secondary-700">{item.category}</span>
                {item.title}
                <span className="text-foreground-400">{item.time}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}